import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Routes } from '../config/routes';
import { ApplicationHttpClient } from '../components/shared/http/application-http-client';

/**
 * Shape accepted by {@link ClientErrorLoggingService.logClientError}. Mirrors
 * the core `POST /client-errors` request contract.
 */
export interface ClientErrorPayload {
    message: string;
    name?: string;
    stack?: string;
    url?: string;
}

/**
 * Wire shape actually POSTed to the backend, with every field truncated to the
 * backend validation max so an oversized stack can never trigger a 422.
 */
interface ClientErrorRequest {
    message: string;
    name?: string;
    stack?: string;
    url?: string;
}

// Backend validation maxima (must match core's POST /client-errors rules).
const MAX_MESSAGE_LENGTH = 2000;
const MAX_NAME_LENGTH = 255;
const MAX_STACK_LENGTH = 10000;
const MAX_URL_LENGTH = 2048;

/**
 * Reports client-side error detail to the backend (`POST /client-errors`).
 *
 * Mirrors `TimeoutLoggingService`: a thin ApplicationHttpClient.post wrapped in
 * a `catchError` that swallows failures so reporting can NEVER throw back into
 * the caller (e.g. the GlobalErrorHandler). A failed report is logged to the
 * console (globally suppressed in production; see main.ts).
 */
@Injectable({
    providedIn: 'root',
})
export class ClientErrorLoggingService
{
    constructor(
            private _http: ApplicationHttpClient,
    ) {}

    public logClientError(error: ClientErrorPayload): Observable<void> {
        const request: ClientErrorRequest = {
            message: this.truncate(error.message, MAX_MESSAGE_LENGTH) ?? '',
        };

        const name = this.truncate(error.name, MAX_NAME_LENGTH);
        if (name) {
            request.name = name;
        }

        const stack = this.truncate(error.stack, MAX_STACK_LENGTH);
        if (stack) {
            request.stack = stack;
        }

        const url = this.truncate(error.url, MAX_URL_LENGTH);
        if (url) {
            request.url = url;
        }

        const endpoint = Routes.MakeLegFiCoreUrl(Routes.LegFiCore.ClientError);

        return this._http.post<void>(endpoint, JSON.stringify(request)).pipe(
                catchError((err) => {
                    // Reporting must never throw — swallow and log locally only
                    // (console is globally suppressed in production; see main.ts).
                    console.error('Failed to log client error:', err);
                    return of(void 0);
                }),
        );
    }

    private truncate(value: string | undefined, max: number): string | undefined {
        if (value === undefined || value === null) {
            return undefined;
        }
        return value.length > max ? value.slice(0, max) : value;
    }
}
